import { MonitoringReasonTypeEnum } from '../enums/monitoringReasonType.enum';
import { SampleStatusEnum } from '../enums/sampleStatus.enum';
import { SampleTypeEnum } from '../enums/sampleType.enum';
import { SampleCategoryEnum } from '../enums/sampleCategory.enum';
import { UnsampledTimeCorrectionType } from '../enums/unsampledTimeCorrectionType.enum';
import { ExposureTypicality } from '../enums/exposureTypicality.enum';
import { SampleShiftCoverage } from '../enums/sampleShiftType.enum';
import { WeatherSourceType } from '../enums/WeatherSourceType.enum';
import { WeatherConditionType } from '../enums/weatherConditionType.enum';
import { FileAttachment } from './fileAttachment.model';
import { IhLabResult } from './IhLabResult.model';
import { Lookup } from './lookup.model';
import { ParameterPercentage } from './parameter.model';
import { PpeAssignment } from './ppeAssignment.model';
import { SampleTasks } from './sampleTasks.model';
import { OvertimeFrequencyEnum } from '../enums/overtimeFrequency.enum';
import { CustomEntity } from './customEntity.model';

export interface SampleEndPoint extends CustomEntity {
    entityType?: string;
    [key: string]: unknown;
}

export class Sample {
    id: string;
    name?: string;
    startDate: Date | string | number;
    endDate: Date | string | number;
    sampleDuration: number;
    sensorAssignmentId: string;
    endPointId: string;
    createdBy: string;
    dateCreated: Date | string | number;
    sampleNumber: string;
    alternateSampleNumber: string;
    sampleSurveyId: string;
    sampleCategory: SampleCategoryEnum;
    isFinished: boolean;
    sampleType: number | SampleTypeEnum;
    segId: string;
    monitoringReason: MonitoringReasonTypeEnum;
    monitoringReasonName?: string;
    isOutlier: boolean;
    unsampledTimeCorrection: UnsampledTimeCorrectionType;
    unsampledTimeCorrectionName?: string;
    monitoringStrategy: number;
    sampleResultFileName: string;
    shiftLength: number;
    exposureTypicality: ExposureTypicality;
    sampleStatus: SampleStatusEnum;
    updatedByUserId: string;
    dateUpdated: Date | number | string;
    organizationLevelId: string;
    surveyName?: string;
    createdByUserId?: string;
    samplingEquipmentId: string;
    calibrationEquipmentId: string;
    ihLabResults?: IhLabResult[];
    sampleTasks?: SampleTasks[];
    segName?: string;
    parameters?: ParameterPercentage[];
    agentIds?: string[];
    selectedGroupAgents?: string[];
    employee?: EmployeeData;
    agentGroupId?: string;
    agentGroupName?: string;
    selectedBy?: number;
    sensorId?: string;
    sensorName?: string;
    sampleMediaId?: string;
    sampleMediaName?: string;
    samplingEquipmentModelNumber?: string;
    samplingEquipmentSerialNumber?: string;
    calibrationEquipmentModelNumber?: string;
    blankSampleId?: string;
    sampledByUserId?: string;
    sampledByUsername?: string;
    managingHygienistId?: string;
    managingHygienistUserName?: string;
    managingHygienistFullName?: string;
    approvedByUserId?: string;
    approvedByUserName?: string;
    approvedByUserEmail?: string;
    approvedDate?: Date | number | string;
    voidReason?: string;
    shiftType?: SampleShiftCoverage;
    summaryNote?: string;
    weatherSource?: WeatherSourceType;
    temperatureMinC?: number;
    temperatureMaxC?: number;
    humidityPercent?: number;
    pressureMmhg?: number;
    weatherCondition?: WeatherConditionType;
    endpointOrganizationLevelId?: string;
    deviceCalibrationHistoryId?: string;
    sampleVolume?: number;
    flowRate?: number;
    correctedVolume?: number;
    outlierReason?: string | null;
    labId?: string | null;
    analyticalMethodId?: string | null;
    analyticalMethodName?: string;
    jurisdictionIds: string[];
    oelTypeId: string;
    fileAttachmentIds?: string[];
    fileAttachments?: FileAttachment[];
    segCode: string;
    organizationLevelName: string;
    organizationLevelCode: string;
    blankSampleNumber: string;
    labRequisitionId: string;
    samplePreCal: number;
    samplePostCal: number;
    sampleAverageCal: number;
    calibrationEquipmentSerialNumber: string;
    calibrationOverdueFlag: boolean;
    locationComments: string;
    deletedByUserId: string;
    deletedDate: string | number;
    isDeleted: boolean;
    runTime: number;
    prePostCalVariabilityFlag: boolean;
    mediaLotNumber: string;
    wipeDimensions: string;
    endPoint?: SampleEndPoint;
    ppeAssignments: PpeAssignment[] | PpeAssignmentPayload[];
    ppeAssignmentList: string[];
    lastCalibrated: string | number;
    calibrationDue: string | number;
    processId: string;
    processCode: string;
    processName: string;
    labRequisition: SampleLabRequisition;
    barcodeContent?: string;
    /* fields To be confirmed */
    averageWeeklyHours_Seconds?: number;
    applyPressureCorrection?: boolean;
    applyShiftCorrection?: boolean;
    shiftRoster?: number;
    exposureAtypicalComments?: string;
    overtimeFrequency?: OvertimeFrequencyEnum;
    symptoms?: boolean;
    symptomComments?: string;
    shiftId: string;
}

export interface SampleLabRequisition {
    id: string;
    labRequisitionNumber: string;
    labName: string;
    dateCreated: string;
    requisitionStatus: number;
    url?: string;
    fileAttachments?: FileAttachment[];
}

export class PpeAssignmentPayload {
    id?: string;
    ppeId: string;
    startDate?: string;
    endDate?: string;
}

export class SampleHistory extends Sample {
    entityId: string;
    dateUpdated: string;
    updatedByUserId: string;
    updatedByUserName: string;
    reason: string;
    changedFields: string;
    changedFieldsArray?: string[];
    createdByUserName?: string;
}

export class EmployeeData extends Lookup {
    [key: string]: unknown;
}

export class LabResult {
    twa?: number;
    agentName?: string;
    agentId?: string;
    twaText?: string;
}

export interface SampleStatusUpdateResult {
    sampleId: string;
    success: boolean;
    error?: string;
}

export interface BulkUpdateResponse<T> {
    result: T;
    successCount: number;
    failureCount: number;
}

export type BulkUpdateSampleStatusResponse = BulkUpdateResponse<SampleStatusUpdateResult[]>;
